// A mastermind's recordings, as Accelerator.dc.html draws its Recordings section (tagged.js has the
// rules): the heading and the line naming their group, Search by topic, the topic chips, then each
// recording with its date tile, title, length and tags, Watch, and Ask about this, which sends Adam
// the question with a link to the recording. A search or topic that finds nothing says so in the
// board's words. While staff view as a member, nothing is sent. Once any of the group's recordings
// has Adam's own thumbnail (decision 47, 12 Oct), each row's date tile becomes its 16:9 frame: the
// thumbnail, or the grey date panel (product/recordingThumbs.js), with the date beside the length where
// it shows the picture; until then the rows are the board's.
//
//   <TaggedRecordings product={productRow} />                          the home's section (h2)
//   <TaggedRecordings product={productRow} headingLevel="h1" months />  the Recordings page, by month
//   limit: at most this many rows while nothing is searched or picked (the home)
//
// On a mastermind, a recording of a session the member missed live and hasn't watched, from their own accountability
// log (the Accelerator's rows: useMastermind), says "You missed this one" in amber beside its title, Watch being its
// main button already (the Accelerator critique's member 9, 20 Oct).
import { useState } from 'react'
import { useMember } from '../app/member.jsx'
import { Link } from '../lib/router.jsx'
import { useViewingAs, readOnlyControl } from '../lib/viewAs.jsx'
import { useProductData } from './ProductData.jsx'
import { askAdam } from './data.js'
import { useMastermind } from '../products/accelerator/useMastermind.js'
import { missedUnwatched } from '../products/accelerator/model.js'
import {
  ASK, MISSED_MARK, SEARCH_LABEL, SEARCH_PLACEHOLDER, TOPIC_CHIPS, byMonth, groupOf, noTopicLine, taggedLine, taggedRows, topicsFrom,
} from './tagged.js'
import { Tick } from './Icons.jsx'
import { RecordingThumb, useRecordingThumbs } from './RecordingThumb.jsx'
import '../styles/tagged.css'

const SEND_FAILED = "That didn't send. Check your connection and try again."

function PlayIcon() {
  return <svg className="icon-fill" width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
}
function SearchIcon() {
  return (
    <svg className="tg-search-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
    </svg>
  )
}

export function TaggedRecordings({ product, headingLevel = 'h2', months = false, limit = null }) {
  const { data } = useProductData()
  const { me } = useMember()
  const [term, setTerm] = useState('')
  const [topic, setTopic] = useState('')
  const p = data?.product || product
  // a mastermind's own log, for the sessions they missed; nothing for any other type
  const mm = useMastermind(p.id, me.person.id, { enabled: p.type === 'mastermind' })
  const missed = p.type === 'mastermind' ? missedUnwatched(mm.data?.log) : null
  const group = groupOf(data)
  const topics = topicsFrom(data).slice(0, TOPIC_CHIPS)
  const filtered = Boolean(term.trim() || topic)
  const all = taggedRows(data, p, { term, topic })
  const rows = !filtered && limit ? all.slice(0, limit) : all
  const H = headingLevel
  const none = taggedRows(data, p).length === 0
  const thumbs = useRecordingThumbs(rows.map(r => r.thumb))

  return (
    <section className="tg" aria-labelledby="tg-title">
      <div className="tg-head">
        <div className="tg-head-row">
          <div className="tg-titles">
            <H className={headingLevel === 'h1' ? 'tg-h tg-h--page' : 'tg-h'} id="tg-title">Recordings</H>
            <span className="tg-line">{taggedLine(group)}</span>
          </div>
          <label className="tg-search">
            <SearchIcon />
            <input type="search" aria-label={SEARCH_LABEL} placeholder={SEARCH_PLACEHOLDER} value={term} onChange={e => setTerm(e.target.value)} />
          </label>
        </div>
        {topics.length > 0 && (
          <div className="tg-topics">
            <span className="tg-topics-label">Topics</span>
            {topics.map(t => (
              <button key={t.tag} type="button" className={topic === t.tag ? 'tg-topic tg-topic--on' : 'tg-topic'}
                aria-pressed={topic === t.tag} onClick={() => setTopic(topic === t.tag ? '' : t.tag)}>
                {t.tag}
              </button>
            ))}
          </div>
        )}
      </div>
      {none && <p className="tg-none">No recordings here yet.</p>}
      {!none && rows.length === 0 && <p className="tg-none">{noTopicLine(group)}</p>}
      {months
        ? byMonth(rows).map(m => (
          <div key={m.month} className="tg-month">
            <h2 className="tg-month-h">{m.month}</h2>
            {m.rows.map(r => <TaggedRow key={r.id} row={r} product={p} thumbs={thumbs} missed={Boolean(missed?.has(r.sessionId) && !r.watched)} />)}
          </div>
        ))
        : rows.map(r => <TaggedRow key={r.id} row={r} product={p} thumbs={thumbs} missed={Boolean(missed?.has(r.sessionId) && !r.watched)} />)}
    </section>
  )
}

function TaggedRow({ row, product, thumbs, missed = false }) {
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const [ask, setAsk] = useState({ open: false, text: '', sent: false, line: null, busy: false })

  async function send() {
    const body = ask.text.trim()
    if (!body || ask.busy || viewingAs || !row.href) return
    setAsk(a => ({ ...a, busy: true, line: null }))
    try {
      await askAdam({
        person_id: me.person.id, product_id: product.id, recording_id: row.id, module_id: null,
        at_seconds: null, chapter_title: null, context_path: row.href, body,
      })
      setAsk({ open: false, text: '', sent: true, line: null, busy: false })
    } catch {
      setAsk(a => ({ ...a, busy: false, line: SEND_FAILED }))
    }
  }

  return (
    <div className="tg-row">
      <div className={row.thumb ? 'tg-row-main tg-row-main--thumb' : 'tg-row-main'}>
        {row.thumb && <RecordingThumb card={row.thumb} thumbs={thumbs} className="tg-thumb" />}
        {!row.thumb && row.block && (
          <span className="tg-date" aria-hidden="true"><span>{row.block.dow}</span><strong>{row.block.day}</strong><span>{row.block.mon}</span></span>
        )}
        <div className="tg-text">
          <div className="tg-title-line">
            <span className="tg-title">{row.title}</span>
            {row.length && <span className="tg-length">{row.length}</span>}
            {row.thumb?.item && row.day && <span className="tg-length tg-day">{row.day}</span>}
            {row.watched && <span className="tg-watched"><Tick />Watched</span>}
            {missed && <span className="tg-missed">{MISSED_MARK}</span>}
          </div>
          {row.tags.length > 0 && (
            <div className="tg-tags">
              {row.tags.map(t => <span key={t.name} className={t.on ? 'tg-tag tg-tag--on' : 'tg-tag'}>{t.name}</span>)}
            </div>
          )}
        </div>
        <span className="tg-actions">
          {row.href && <Link className="tg-watch" to={row.href} aria-label={`Watch ${row.title}`}><PlayIcon />Watch</Link>}
          <button type="button" className="tg-ask-btn" aria-expanded={ask.open}
            onClick={() => setAsk(a => ({ ...a, open: !a.open, sent: false }))} {...readOnlyControl(viewingAs)}>
            {ASK.button}
          </button>
        </span>
      </div>
      {ask.open && !viewingAs && (
        <div className="tg-ask">
          <textarea aria-label="Your question" placeholder={ASK.placeholder} value={ask.text}
            onChange={e => setAsk(a => ({ ...a, text: e.target.value }))} />
          <div className="tg-ask-foot">
            <span className="tg-ask-note">{ask.line || ASK.note}</span>
            <button type="button" className="tg-ask-send" onClick={send} aria-busy={ask.busy || undefined}>{ASK.send}</button>
          </div>
        </div>
      )}
      {ask.sent && <p className="tg-sent" role="status">{ASK.sent}</p>}
    </div>
  )
}
